import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Building2, Users, Calendar, Trophy, Mail, 
  Clock, Ban, CheckCircle, RefreshCw
} from 'lucide-react';

// Helper to get auth config for axios requests (mobile compatibility)
const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

export default function SuperAdminTenantDetail() {
  const { user, API } = useContext(AppContext);
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [members, setMembers] = useState([]);
  const [league, setLeague] = useState(null);
  const [seasons, setSeasons] = useState([]);

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate('/home');
      return;
    }
    loadTenantDetails();
  }, [user, tenantId]);

  const loadTenantDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/platform/tenants/${tenantId}`, getAuthConfig());
      setTenant(response.data.tenant);
      setMembers(response.data.members || []);
      setLeague(response.data.league);
      setSeasons(response.data.seasons || []);
    } catch (error) {
      console.error('Failed to load tenant:', error);
      toast.error('Failed to load tenant');
      navigate('/super-admin/tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!window.confirm('Suspend this tenant?')) return;
    try {
      await axios.post(`${API}/platform/tenants/${tenantId}/suspend`, null, {
        ...getAuthConfig(),
        params: { reason: 'Suspended by administrator' }
      });
      toast.success('Tenant suspended');
      loadTenantDetails();
    } catch (error) {
      toast.error('Failed to suspend');
    }
  };

  const handleUnsuspend = async () => {
    try {
      await axios.post(`${API}/platform/tenants/${tenantId}/unsuspend`, {}, getAuthConfig());
      toast.success('Tenant reactivated');
      loadTenantDetails();
    } catch (error) {
      toast.error('Failed to unsuspend');
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      league_admin: <Badge className="bg-amber-600 text-xs">Admin</Badge>,
      moderator: <Badge className="bg-blue-600 text-xs">Mod</Badge>,
      player: <Badge variant="outline" className="text-zinc-400 text-xs">Player</Badge>
    };
    return badges[role] || badges.player;
  };

  if (!user?.is_super_admin) return null;

  if (loading) {
    return (
      <SuperAdminLayout title="Tenant Details" backLink="/super-admin/tenants">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
        </div>
      </SuperAdminLayout>
    );
  }

  if (!tenant) {
    return (
      <SuperAdminLayout title="Tenant Details" backLink="/super-admin/tenants">
        <div className="text-center py-12 text-zinc-400">Tenant not found</div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout title={tenant.name} subtitle={tenant.owner?.email} backLink="/super-admin/tenants" onRefresh={loadTenantDetails}>
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Tenant Header Card */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-900 rounded-lg flex items-center justify-center flex-shrink-0">
                <Building2 className="w-6 h-6 text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-lg sm:text-xl font-bold text-white truncate">{tenant.name}</h1>
                  <Badge className={tenant.status === 'active' ? 'bg-emerald-600 text-xs' : 'bg-red-600 text-xs'}>
                    {tenant.status}
                  </Badge>
                </div>
                <p className="text-sm text-zinc-400 truncate">{league?.name || tenant.name}</p>
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="flex gap-2 mt-4 pt-4 border-t border-zinc-800">
              {tenant.status === 'active' ? (
                <Button variant="destructive" size="sm" onClick={handleSuspend} className="w-full sm:w-auto">
                  <Ban className="w-4 h-4 mr-1" /> Suspend
                </Button>
              ) : (
                <Button size="sm" onClick={handleUnsuspend} className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto">
                  <CheckCircle className="w-4 h-4 mr-1" /> Reactivate
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-xs sm:text-sm">Members</p>
                  <p className="text-xl sm:text-2xl font-bold text-white">{members.length}</p>
                </div>
                <Users className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-xs sm:text-sm">Seasons</p>
                  <p className="text-xl sm:text-2xl font-bold text-white">{seasons.length}</p>
                </div>
                <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Owner Info */}
        {tenant.owner && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-2 sm:pb-4">
              <CardTitle className="text-white text-base sm:text-lg">Owner</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  {tenant.owner.name?.[0]?.toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-white text-sm truncate">{tenant.owner.name}</p>
                  <p className="text-xs text-zinc-400 truncate flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {tenant.owner.email}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Members List */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2 sm:pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-base sm:text-lg">Members</CardTitle>
              <Button variant="ghost" size="sm" onClick={loadTenantDetails} className="text-zinc-400">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {members.length === 0 ? (
              <p className="text-zinc-400 text-sm text-center py-6">No members</p>
            ) : (
              <div className="space-y-2">
                {members.map(member => (
                  <div key={member.id} className="flex items-center justify-between p-2 sm:p-3 bg-zinc-800 rounded-lg">
                    <div className="flex items-center gap-2 min-w-0">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                        style={{ backgroundColor: member.color || '#3b82f6' }}
                      >
                        {member.name?.[0]?.toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-sm truncate">{member.name}</p>
                        <p className="text-xs text-zinc-400 truncate">{member.email}</p>
                      </div>
                    </div>
                    {getRoleBadge(member.role)}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Seasons List */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2 sm:pb-4">
            <CardTitle className="text-white text-base sm:text-lg">Seasons</CardTitle>
          </CardHeader>
          <CardContent>
            {seasons.length === 0 ? (
              <p className="text-zinc-400 text-sm text-center py-6">No seasons</p>
            ) : (
              <div className="space-y-2">
                {seasons.map(season => (
                  <div key={season.id} className="flex items-center justify-between p-2 sm:p-3 bg-zinc-800 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-amber-400" />
                      <span className="text-white text-sm">Season {season.number}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-zinc-400 text-xs">{season.status}</Badge>
                      <span className="text-xs text-zinc-500 hidden sm:block">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {new Date(season.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </SuperAdminLayout>
  );
}
